import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';
import LanguageToggle from '../components/LanguageToggle';
import UserAvatar from '../components/UserAvatar';
import InviteWhatsAppCard from '../components/InviteWhatsAppCard';
import AuraScore from '../components/AuraScore';
import LevelProgress from '../components/LevelProgress';
import BadgeGrid from '../components/BadgeGrid';
import AuraQR from '../components/AuraQR';
import { winRate, formatAura } from '../lib/aura';
import { resolveMediaUrl } from '../lib/social';
import { toast } from 'sonner';
import { ChevronLeft, LogOut, Pencil, UserPlus, UserCheck, Swords, MapPin, Zap } from 'lucide-react';

/**
 * Profile page — used for:
 *   /me           (own private view, editable, shows aura history)
 *   /u/:username  (public view of any user)
 */
export default function ProfilePage({ mode = 'me' }) {
  const { username: paramUsername } = useParams();
  const { user: me, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyFollow, setBusyFollow] = useState(false);
  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);

  const targetUsername = mode === 'me' ? me?.username : paramUsername;

  useEffect(() => {
    if (!targetUsername) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: publicData } = await api.get(`/profile/username/${targetUsername}`);
        if (cancelled) return;
        setData(publicData);
        if (mode === 'me') {
          const { data: tx } = await api.get('/aura/transactions', { params: { limit: 25 } });
          if (!cancelled) setTransactions(tx.items || []);
        }
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || t('common.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [targetUsername, mode, t]);

  // Load posts for the active user (POSTS tab)
  useEffect(() => {
    if (!data?.user?.username) return;
    if (tab !== 'posts') return;
    let cancelled = false;
    (async () => {
      setPostsLoading(true);
      try {
        const { data: r } = await api.get(`/profile/username/${data.user.username}/posts`, { params: { limit: 24 } });
        if (!cancelled) setPosts(r.items || []);
      } catch (_e) { /* ignore */ }
      finally { if (!cancelled) setPostsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [data?.user?.username, tab]);

  const toggleFollow = async () => {
    if (!me) { navigate('/auth'); return; }
    if (!data?.user) return;
    setBusyFollow(true);
    try {
      if (data.is_following) {
        await api.delete(`/follow/${data.user.username}`);
        setData((d) => ({
          ...d,
          is_following: false,
          user: { ...d.user, followers_count: Math.max(0, d.user.followers_count - 1) },
        }));
        toast.success(t('toast.unfollowed'));
      } else {
        await api.post(`/follow/${data.user.username}`);
        setData((d) => ({
          ...d,
          is_following: true,
          user: { ...d.user, followers_count: d.user.followers_count + 1 },
        }));
        toast.success(t('toast.followed'));
      }
    } catch (_e) {
      toast.error(t('toast.error'));
    } finally { setBusyFollow(false); }
  };

  const doLogout = async () => {
    await logout();
    navigate('/auth', { replace: true });
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-aura-bg grid place-items-center">
        <div className="text-4xl animate-aura-pulse">⚡</div>
      </div>
    );
  }

  const u = data.user;
  const wr = winRate(u);

  return (
    <div className="min-h-screen bg-aura-bg pb-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />

      <header className="relative flex items-center justify-between px-5 pt-6 pb-2">
        {mode === 'me' ? (
          <div className="flex items-center gap-2">
            <span className="font-display font-black text-lg tracking-[0.3em] aura-text-gradient">AURA</span>
          </div>
        ) : (
          <button onClick={() => navigate(-1)} data-testid="btn-back" className="text-aura-textDim hover:text-white flex items-center gap-1">
            <ChevronLeft size={20} /> {t('common.back')}
          </button>
        )}
        <div className="flex items-center gap-2">
          <LanguageToggle />
          {mode === 'me' && (
            <>
              <Link to="/me/edit" data-testid="btn-edit-profile" className="aura-btn-ghost !py-1.5 !px-3 text-xs">
                <Pencil size={14} /> {t('profile.edit')}
              </Link>
              <button onClick={doLogout} data-testid="btn-logout" className="aura-btn-ghost !py-1.5 !px-3 text-xs" title={t('common.logout')}>
                <LogOut size={14} />
              </button>
            </>
          )}
        </div>
      </header>

      <main className="relative mx-auto max-w-md px-5 space-y-5 pt-4">
        {/* Banner + avatar */}
        <section className="aura-card overflow-hidden">
          <div className="h-24 bg-aura-gradient/40 relative">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1778246465740-371661438f08?auto=format&fit=crop&w=1200&q=60')] bg-cover bg-center opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-aura-surface" />
          </div>
          <div className="px-5 pb-5 -mt-10 relative">
            <div className="flex items-end justify-between">
              <UserAvatar user={u} size="xl" />
              {mode !== 'me' && me && !data.is_self && (
                <div className="flex items-center gap-2 mb-1">
                  <button
                    onClick={toggleFollow}
                    disabled={busyFollow}
                    data-testid={data.is_following ? 'btn-unfollow' : 'btn-follow'}
                    className={data.is_following ? 'aura-btn-ghost !py-2 !px-4' : 'aura-btn-primary !py-2 !px-4 !text-sm'}
                  >
                    {data.is_following ? <><UserCheck size={16} /> {t('profile.following')}</> : <><UserPlus size={16} /> {t('profile.follow')}</>}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const { data: r } = await api.post('/battles/challenge', { opponent_username: u.username });
                        toast.success(t('scan.challengeSent'));
                        navigate(`/battles?pending=${r.challenge_id}`);
                      } catch (err) { toast.error(err?.response?.data?.detail || t('common.error')); }
                    }}
                    data-testid="btn-challenge"
                    className="aura-btn-ghost !py-2 !px-3 !text-sm"
                    title={t('profile.challenge')}
                  >
                    <Swords size={14} />
                  </button>
                </div>
              )}
            </div>
            <div className="mt-3">
              <div className="font-display font-black text-2xl text-white leading-tight" data-testid="profile-name">{u.name}</div>
              <div className="text-aura-textDim text-sm" data-testid="profile-username">@{u.username}</div>
              {(u.city || u.country) && (
                <div className="mt-1 text-xs text-aura-textDim flex items-center gap-1">
                  <MapPin size={12} /> {[u.city, u.state, u.country].filter(Boolean).join(', ')}
                </div>
              )}
              {u.bio && <p className="mt-3 text-sm text-white/85 whitespace-pre-wrap">{u.bio}</p>}
            </div>
          </div>
        </section>

        {/* Aura score */}
        <section className="aura-card p-6 text-center relative overflow-hidden grain">
          <div className="absolute -top-16 -right-16 h-52 w-52 rounded-full bg-aura-violet/20 blur-3xl" />
          <div className="text-[10px] tracking-[0.3em] text-aura-textDim mb-2">AURA POINTS</div>
          <AuraScore points={u.aura_points} size="xl" testId="profile-aura-score" />
          <div className="mt-6">
            <LevelProgress points={u.aura_points} />
          </div>
        </section>

        {/* Stats row 1 */}
        <section className="grid grid-cols-3 gap-3">
          <StatBox testId="stat-followers" label={t('profile.followers')} value={u.followers_count} />
          <StatBox testId="stat-following" label={t('profile.followingLabel')} value={u.following_count} />
          <StatBox testId="stat-posts" label={t('profile.posts')} value={u.posts_count} />
        </section>

        {/* Battle stats */}
        <section className="aura-card p-4">
          <div className="text-[10px] tracking-[0.2em] text-aura-textDim font-display mb-3">{t('profile.battles').toUpperCase()}</div>
          <div className="grid grid-cols-3 gap-3">
            <StatBox testId="stat-battles" label={t('profile.battles')} value={u.battles} inline />
            <StatBox testId="stat-wins" label={t('profile.wins')} value={u.wins} inline color="#00FFA3" />
            <StatBox testId="stat-losses" label={t('profile.losses')} value={u.losses} inline color="#FF2E9F" />
            <StatBox testId="stat-ties" label={t('profile.ties')} value={u.ties} inline />
            <StatBox testId="stat-winrate" label={t('profile.winRate')} value={`${wr}%`} inline color="#FFE800" />
            <StatBox testId="stat-streak" label={t('profile.streak')} value={u.current_streak} inline />
          </div>
          <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 gap-3">
            <StatBox testId="stat-best-streak" label={t('profile.bestStreak')} value={u.best_streak} inline />
            <StatBox testId="stat-local-rank" label={t('profile.localRank')} value="—" inline muted />
          </div>
        </section>

        {/* QR — only for own profile */}
        {mode === 'me' && (
          <>
            <AuraQR username={u.username} />
            <div className="grid grid-cols-2 gap-2">
              <Link to="/me/qr" data-testid="link-my-aura-qr" className="aura-btn-primary !py-3 justify-center">⚡ Mi QR</Link>
              <Link to="/me/battle-style" data-testid="link-battle-style" className="aura-btn-ghost !py-3 justify-center">🥊 Battle Style</Link>
            </div>
            <InviteWhatsAppCard user={u} />
          </>
        )}

        {/* Tabs */}
        <section className="border-b border-white/5">
          <div className="flex" data-testid="profile-tabs">
            {[
              { k: 'posts',   label: t('profile.tabs.posts') },
              { k: 'battles', label: t('profile.tabs.battles') },
              { k: 'aura',    label: t('profile.tabs.aura') },
              { k: 'badges',  label: t('profile.tabs.badges') },
            ].map((x) => (
              <button
                key={x.k} onClick={() => setTab(x.k)}
                data-testid={`profile-tab-${x.k}`}
                className={`flex-1 py-2.5 text-xs font-display font-bold tracking-widest ${tab === x.k ? 'text-aura-cyan border-b-2 border-aura-cyan' : 'text-aura-textDim'}`}
              >
                {x.label}
              </button>
            ))}
          </div>
        </section>

        {tab === 'posts' && (
          <section data-testid="profile-posts">
            {postsLoading ? (
              <div className="text-center py-8 animate-aura-pulse text-3xl">⚡</div>
            ) : posts.length === 0 ? (
              <div className="aura-card p-6 text-center text-sm text-aura-textDim">{t('profile.noPosts')}</div>
            ) : (
              <div className="grid grid-cols-3 gap-1.5">
                {posts.map((p) => (
                  <Link key={p.post_id} to={`/post/${p.public_post_id}`} className="aspect-[9/16] relative overflow-hidden rounded-lg bg-aura-surface2 border border-white/5">
                    {p.media?.[0] ? (
                      <img src={resolveMediaUrl(p.media[0].thumbnail_url || p.media[0].url)} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center p-2 text-[11px] text-white/85 text-center">{(p.text_body || p.caption || '').slice(0, 80)}</div>
                    )}
                    <div className="absolute bottom-1 left-1 right-1 flex items-center gap-1 text-white text-[10px]">
                      <Zap size={10} className="text-aura-cyan" />
                      <span className="font-mono">{formatAura(p.reactions_total || 0)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === 'battles' && (
          <section className="aura-card p-8 text-center text-sm text-aura-textDim">{t('common.comingSoon')}</section>
        )}

        {tab === 'aura' && mode === 'me' && (
          <section>
            <h3 className="font-display font-bold text-white tracking-wide mb-2">{t('profile.auraHistory')}</h3>
            <div className="aura-card divide-y divide-white/5" data-testid="aura-transactions">
              {transactions.length === 0 ? (
                <div className="p-4 text-sm text-aura-textDim">{t('profile.noTransactions')}</div>
              ) : (
                transactions.map((tx) => (
                  <div key={tx.transaction_id} className="p-3 flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="text-white/90 truncate">{tx.reason}</div>
                      <div className="text-[11px] text-aura-textDim">{tx.source_type}{tx.source_id ? ` · ${tx.source_id}` : ''}</div>
                    </div>
                    <div className={`font-mono font-semibold ${tx.type === 'credit' ? 'text-aura-green' : tx.type === 'debit' ? 'text-aura-pink' : 'text-aura-textDim'}`}>
                      {tx.type === 'credit' ? '+' : tx.type === 'debit' ? '-' : ''}{formatAura(tx.amount)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {tab === 'aura' && mode !== 'me' && (
          <section className="aura-card p-8 text-center text-sm text-aura-textDim">{t('common.comingSoon')}</section>
        )}

        {tab === 'badges' && (
          <section data-testid="badges-grid">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-white tracking-wide">{t('profile.badges')}</h3>
              <span className="text-xs text-aura-textDim">{(data.badges || []).length}</span>
            </div>
            <BadgeGrid badges={data.badges || []} />
          </section>
        )}
      </main>

      <BottomNav />
    </div>
  );
}

function StatBox({ label, value, testId, color, inline, muted }) {
  return (
    <div data-testid={testId} className={`${inline ? '' : 'aura-card p-3'} text-center`}>
      <div className="font-display font-bold text-lg" style={{ color: color || '#FFFFFF' }}>{value ?? 0}</div>
      <div className={`text-[10px] uppercase tracking-widest ${muted ? 'text-white/30' : 'text-aura-textDim'}`}>{label}</div>
    </div>
  );
}
